import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CircularProgress, Grid, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { useMutation, useQuery } from "@tanstack/react-query";
import ShipeeziGrid, {
  Column,
  Constants,
  GeneralActions,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import NewRecord from "Components/AppGrid/shipeezi-new-grid/TopPanel/NewRecord";
import { colors } from "@zapalacorp/tokens";
import { AxiosError } from "axios";
import { VipChip } from "Components/Generic/VipChip";
import { format as formatDate, isValid } from "date-fns";
import { useActionFolders } from "Hooks/useActionFolders";
import { Branch } from "Models/branch";
import { useTranslation } from "react-i18next";
import { FiActivity, FiEdit, FiEye, FiPrinter, FiTruck } from "react-icons/fi";
import { HiOutlineMail } from "react-icons/hi";
import { MdOutlineCancel } from "react-icons/md";
import { useHistory } from "react-router-dom";
import { getJobName } from "Util/getJobName";
import { getOrderCustomerDisplayName } from "Util/names";
import { getResourceByNameGroup } from "Util/getResourceByNameGroup";
import * as yup from "yup";
import { ObjectShape } from "yup/lib/object";

import ConfirmationModal from "../../../../components/Dialogs/ConfirmationModal";
import { Color } from "../../../../components/Form/Button";
import Chip from "../../../../components/Form/Chip";
import Input from "../../../../components/Form/Input";
import AccessTab from "../../../../components/Generic/Access/ViewMode";
import BadgeJobStatus from "../../../../components/Generic/BadgeJobStatus";
import BadgeType from "../../../../components/Generic/BadgeType";
import Files from "../../../../components/Generic/Files";
import History from "../../../../components/Generic/History";
import Instructions from "../../../../components/Generic/Instructions";
import PackingMaterials from "../../../../components/Generic/PackingMaterials";
import { NotificationToast } from "../../../../components/Generic/Toast/NotificationToast";
import { DefaultTooltip } from "../../../../components/Generic/Tooltip";
import SideViewComponent, {
  Navigation,
  PropHandles,
} from "../../../../components/Generic/ViewJobComponent";
import TitlePage from "../../../../components/TitlePage";
import {
  GO_SHARE_COMPANY_IDS,
  PRIVATE_ROUTES,
  SELECTED_COLUMNS_ORDERS,
} from "../../../../constants";
import { useAppState } from "../../../../context/StateContext";
import { useActionsJob } from "../../../../hooks/useActionJob";
import useYupValidation from "../../../../hooks/useYupValidation";
import { Customer } from "../../../../models/customer";
import { Job, JobLocation } from "../../../../models/job";
import { JobType } from "../../../../models/jobType";
import { LeadSource } from "../../../../models/leadsource";
import { Location } from "../../../../models/location";
import { Order } from "../../../../models/order";
import { Filters, Searches } from "../../../../models/shipeezi.table";
import { Statuses } from "../../../../models/statuses";
import { Task } from "../../../../models/task";
import { User } from "../../../../models/user";
import AmosService from "../../../../services/amos.service";
import JobService from "../../../../services/job.service";
import JobTypeService from "../../../../services/job.type.service";
import LeadSourceFromService from "../../../../services/lead.source.from.service";
import OrderService from "../../../../services/order.service";
import StatusesService from "../../../../services/statuses.service";
import { isOrderNeitherDeliveredNorCancelled } from "../../../../util/actions";
import { calculateTotalVolume } from "../../../../util/calculateTotalVolume";
import { checkError } from "../../../../util/checkError";
import { handleGetConvertValue, Metric } from "../../../../util/converts";
import {
  ItemJobSubStatus,
  JobStatus as OrderStatus,
  OrderVipTreatmentMode,
  ResourceGroup,
  ResourceName,
  ResourceSubGroup,
  Status,
  SubStatusUsedBy,
  TaskStatus,
} from "../../../../util/enums";
import env from "../../../../util/env";
import { formatJobLocation } from "../../../../util/formatItemLocation";
import { getOrderFolder } from "../../../../util/getFolders";
import getItemsToHistory from "../../../../util/getHistory";
import { getMeasurement } from "../../../../util/getMeasurement";
import { handlesGrid } from "../../../../util/handlesGrid";
import { isAllied } from "../../../../util/isAllied";
import GenerateReportModal, { GenerateReportModalHandler } from "../Shared/GenerateReportModal";
import LoadOrderConfirmationModal, {
  LoadOrderConfirmationModalHandles,
} from "./ChangeOrderStatusConfirmationModal";
import { SkeletonDetails } from "./Maintenance/Details";
import Details from "./Maintenance/Details/ViewMode";
import OrderInventory from "./Maintenance/Inventory";
import TasksTab from "./Maintenance/Tasks";
import WaybillTab from "./Maintenance/Waybill";
import SendToCustomerModalOrder, { SendToCustomerOrderHandle } from "./SendToCustomerOrder";
import useStyles, { LoadOrderIcon, TagWrapper } from "./styles";
import TasksInProgressModal from "./TasksInProgressModal";
import { GenerateReportDrawer, GenerateReportDrawerHandles } from "Components/GenerateReport";
import {
  normalizeOrderWebPayload,
} from "Util/normalizeOrderWebPayload";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceCanEnum } from "Util/enums/accessRule";
import { isMoving } from "Util/validation";
import { CompanyConfigsService } from "Services/company-configs.service";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";
import { useQuickOrderFeature } from "Hooks/useQuickOrderFeature";

const STALE_TIME = 1 * 60 * 1000; // 1 minute
const CACHE_TIME = 1 * 60 * 60 * 1000; // 1 hour

type Props = {
  classNameContent?: string;
  className?: string;
  viewOnly?: boolean;
  showTitle?: boolean;
  isRefetchGetAll?: boolean;
  handleNewRecord?: () => void;
  handleEdit?: (id: number, order: Job) => void;
  customFilter?: { [x: string]: string | number | undefined };
};

const OrderOperations = (props: Props) => {
  const {
    className,
    classNameContent,
    viewOnly,
    showTitle,
    handleNewRecord,
    handleEdit,
    customFilter,
    isRefetchGetAll,
  } = props;

  const { currentUser, userPermissions, setAgentPermissions, agentPermissions } = useAppState();
  const { quickOrderEnabled } = useQuickOrderFeature();
  const { t: translate } = useTranslation();
  const history = useHistory();
  const classes = useStyles();
  const sideViewRef = useRef<PropHandles>(null);
  const modalRef = useRef<GenerateReportModalHandler>(null);
  const loadOrderConfirmationModalRef = useRef<LoadOrderConfirmationModalHandles>(null);
  const generateReportRef = useRef<GenerateReportDrawerHandles>(null);

  const operationOrdersPermissions = getResourceByNameGroup(
    ResourceGroup.OPERATIONS,
    ResourceSubGroup.OPERATIONS,
    ResourceName.ORDERS,
    currentUser?.resources
  )?.roles_resources;

  const measurement = getMeasurement();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);

  const [orderIdToDelivered, setOrderIdToDelivered] = useState<number | null>(null);

  const [cancelReason, setCancelReason] = useState<string>("");
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);
  const [isSubmitted, setIsSubmitted] = useState<boolean>();

  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);

  const [sorting, setSorting] = useState<Array<string[]>>();
  const [disableCustomerSorting, setDisableCustomerSorting] = useState<boolean>(false);

  const [orderToView, setOrderToView] = useState<Job>();
  const [order, setOrder] = useState<Order>();
  const [typeFolder, setTypeFolder] = useState<"Orders" | "Quote">("Orders");

  const [jobStatus, setJobStatus] = useState<Statuses[]>([]);
  const [jobTypes, setJobTypes] = useState<JobType[]>([]);

  const [idsGeneratingReport] = useState<number[]>([]);

  const [orderDelete, setOrderDelete] = useState<Job>();

  const [openWarningTasksInProgress, setOpenWarningTasksInProgress] = useState<boolean>(false);

  const [tasksInProgress, setTasksInProgress] = useState<Task[]>();

  const [openConfirmationDeleteModal, setOpenConfirmationDeleteModal] = useState<boolean>(false);

  const [anchorMoreIcon, setAnchorMoreIcon] = useState<null | HTMLElement>(null);

  const [orderIdSend, setOrderIdSend] = useState<number>();

  const [leadSourceFrom, setLeadSourceFrom] = useState<LeadSource[]>([]);

  const { handleCurrentPage, handleFilter, handlePageSize, handleSearch, handleSort } = handlesGrid(
    { setFilters, setSorting, setPage, setPageSize, setSearches }
  );

  const { getInventoryList, getJobHistories } = useActionsJob({
    jobId: orderToView?.id,
  });

  const { data: inventoryData, isLoading: isLoadingInventory } = getInventoryList([
    ItemJobSubStatus.QUOTED,
  ]);

  const { data: actualInventoryData, isLoading: isLoadingActualInventory } = getInventoryList([
    ItemJobSubStatus.ACTUAL,
  ]);

  const { data: historyData } = getJobHistories();

  const { getOrderFolders, getQuoteFolders } = useActionFolders({ jobId: orderToView?.id });

  const { data: orderFolderData, isFetching: isOrderFolderData } = getOrderFolders();
  const { data: quoteFolderData, isFetching: isQuoteFolderData } = getQuoteFolders();

  const communicationModalRef = useRef<SendToCustomerOrderHandle>(null);

  const savedColumns = window.localStorage.getItem(SELECTED_COLUMNS_ORDERS);
  const columnsToShow: string[] = savedColumns ? JSON.parse(savedColumns) : [];
  const [orderPermission, setOrderPermission] = useState<{
    view: boolean;
    edit: boolean;
    create: boolean;
    videoSurvey: boolean;
    communication: boolean;
    approve: boolean;
    decline: boolean;
    cancel: boolean;
    delivery: boolean;
    printReport: boolean;
  }>({
    view: false,
    edit: false,
    create: false,
    videoSurvey: false,
    communication: false,
    approve: false,
    decline: false,
    cancel: false,
    delivery: false,
    printReport: false,
  });

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setOrderPermission({
        view: hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_ORDERS_VIEW),
        edit:
          checkResourceCan(userPermissions, ResourceName.ORDERS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_ORDERS_EDIT),
        create:
          checkResourceCan(userPermissions, ResourceName.ORDERS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_ORDERS_ADD_NEW),
        videoSurvey:
          checkResourceCan(userPermissions, ResourceName.ORDERS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_ORDERS_VIDEO_SURVEY),
        communication:
          checkResourceCan(userPermissions, ResourceName.ORDERS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_ORDERS_COMMUNICATION),
        approve:
          checkResourceCan(userPermissions, ResourceName.ORDERS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_ORDERS_APPROVE),
        decline:
          checkResourceCan(userPermissions, ResourceName.ORDERS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_ORDERS_DECLINE),
        cancel:
          checkResourceCan(userPermissions, ResourceName.ORDERS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_ORDERS_CANCEL_ORDER),
        delivery:
          checkResourceCan(userPermissions, ResourceName.ORDERS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_ORDERS_DELIVERY),
        printReport:
          checkResourceCan(userPermissions, ResourceName.ORDERS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_ORDERS_PRINT_REPORT),
      });
    }
  }, [userPermissions]);

  const hasAgentPermission = (job: Job | undefined, action: string): boolean => {
    // console.log(`Checking if agent can see action ${action} for job ${job?.id}`);
    if (!job) {
      return false;
    }
    if (job.companyId === currentUser?.companyId) {
      // console.log(`Job ${job?.id} belongs to the same company as the current user`);
      return true;
    }

    if (job.companyId !== currentUser?.companyId) {
      if (!["edit", "view"].includes(action)) {
        // console.log(`Action ${action} is not allowed for agent jobs`);
        return false;
      }

      const jobSpecificConfig = agentPermissions?.[job.id];
      // !!jobSpecificConfig
      //   ? console.log(`Job ${job?.id} has specific config`)
      //   : console.log(`Job ${job?.id} has no specific config`);
      switch (action) {
        case "edit":
          return jobSpecificConfig
            ? !!jobSpecificConfig.order.sections.inventory.fields.actualInventory.edit ||
                !!jobSpecificConfig.order.sections.material.fields.general.edit ||
                !!jobSpecificConfig.order.sections.task.fields.general.edit ||
                !!jobSpecificConfig.order.sections.access.fields.general.edit ||
                !!jobSpecificConfig.order.sections.instruction.fields.general.edit ||
                !!jobSpecificConfig.order.sections.folder.fields.documents.edit ||
                !!jobSpecificConfig.order.sections.folder.fields.photosVideos.edit
            : false;
        case "view":
          return jobSpecificConfig
            ? !!jobSpecificConfig.order.sections.detail.fields.roles.manager ||
                !!jobSpecificConfig.order.sections.detail.fields.roles.consultant ||
                !!jobSpecificConfig.order.sections.detail.fields.roles.bookingAgent ||
                !!jobSpecificConfig.order.sections.detail.fields.roles.branchOrigin ||
                !!jobSpecificConfig.order.sections.detail.fields.roles.moveCoordinator ||
                !!jobSpecificConfig.order.sections.detail.fields.roles.branchDestination ||
                !!jobSpecificConfig.order.sections.detail.fields.pickup.address ||
                !!jobSpecificConfig.order.sections.detail.fields.pickup.contact ||
                !!jobSpecificConfig.order.sections.detail.fields.details.tag ||
                !!jobSpecificConfig.order.sections.detail.fields.details.mode ||
                !!jobSpecificConfig.order.sections.detail.fields.details.scac ||
                !!jobSpecificConfig.order.sections.detail.fields.details.orderType ||
                !!jobSpecificConfig.order.sections.detail.fields.details.reference ||
                !!jobSpecificConfig.order.sections.detail.fields.details.leadSource ||
                !!jobSpecificConfig.order.sections.detail.fields.details.accountType ||
                !!jobSpecificConfig.order.sections.detail.fields.details["2ndReference"] ||
                !!jobSpecificConfig.order.sections.detail.fields.details.vipTreatment ||
                !!jobSpecificConfig.order.sections.detail.fields.details.transportationType ||
                !!jobSpecificConfig.order.sections.detail.fields.delivery.address ||
                !!jobSpecificConfig.order.sections.detail.fields.delivery.contact ||
                !!jobSpecificConfig.order.sections.detail.fields.generalComments.view ||
                !!jobSpecificConfig.order.sections.detail.fields.weightMeasurements.allowance ||
                !!jobSpecificConfig.order.sections.detail.fields.weightMeasurements
                  .inventorySummary ||
                !!jobSpecificConfig.order.sections.inventory.fields.actualInventory.view ||
                !!jobSpecificConfig.order.sections.inventory.fields.quotedInventory.view ||
                !!jobSpecificConfig.order.sections.material.fields.general.view ||
                !!jobSpecificConfig.order.sections.task.fields.general.view ||
                !!jobSpecificConfig.order.sections.access.fields.general.view ||
                !!jobSpecificConfig.order.sections.instruction.fields.general.view ||
                !!jobSpecificConfig.order.sections.folder.fields.documents.view ||
                !!jobSpecificConfig.order.sections.folder.fields.photosVideos.view
            : false;
      }
    }

    return false;
  };

  useEffect(() => {
    if (isSubmitted) {
      handleValidateCancelReason();
    }
  }, [cancelReason]);

  useEffect(() => {
    if (orderDelete && orderDelete?.id) {
      if (orderDelete?.tasks && orderDelete?.tasks.length > 0) {
        const tasksInProgress = orderDelete?.tasks.filter(
          (task) =>
            task.status !== TaskStatus.FINISHED &&
            task.status !== TaskStatus.CANCELLED &&
            task.status !== TaskStatus.REJECTED
        );

        if (tasksInProgress && tasksInProgress.length > 0) {
          setOpenWarningTasksInProgress(true);
          setTasksInProgress(tasksInProgress);
        } else {
          setOpenConfirmationDeleteModal(true);
        }
      } else {
        setOpenConfirmationDeleteModal(true);
      }
    }
  }, [orderDelete]);

  useQuery(
    ["statuses.list.order"],
    () =>
      StatusesService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        usedBy: SubStatusUsedBy.ORDERS,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          setJobStatus(response.data);
        }
      },
    }
  );

  useQuery(
    ["jobType.list"],
    () =>
      JobTypeService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) setJobTypes(response.data);
      },
    }
  );

  useQuery(
    ["leadSource.list"],
    () =>
      LeadSourceFromService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (response && response.data?.length) setLeadSourceFrom(response.data);
      },
    }
  );

  const branchesList = useMemo(() => {
    if (currentUser && currentUser?.branches && currentUser?.branches?.length) {
      return currentUser?.branches?.sort((branchA, branchB) =>
        (branchA?.name || "").localeCompare(branchB?.name || "")
      );
    }

    return [] as Branch[];
  }, [currentUser]);


  useQuery(
    ["company.configs.orderSideView", orderToView?.id],
    async () => {
      if (!orderToView?.id) return null;
      const response = await CompanyConfigsService.getByCreatedCompany([Number(orderToView.id)]);
      const permission = response?.data;
      return permission ?? null;
    },
    {
      enabled: !!orderToView?.id,
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: orders,
    isFetching: isLoading,
    refetch,
  } = useQuery(
    ["order.list", page, pageSize, sorting, filters, customFilter, searches, currentUser?.branches],
    async () => {
      const response = await OrderService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        include: JSON.stringify([{ association: "itemsJob" }]),
        sortBy:
          sorting && sorting?.length > 0
            ? JSON.stringify(sorting)
            : JSON.stringify([["updatedAt", "DESC"]]),
        userId: currentUser?.id,
        branchId: !GO_SHARE_COMPANY_IDS.includes(currentUser?.companyId!)
          ? currentUser?.branches
              ?.filter((branch) => branch.companyId === currentUser.companyId)
              ?.map((branch) => branch.id)
          : [],
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...(customFilter || {}),
        ...filters,
      });

      if (response && response?.data) return response?.data as Job[];

      return [];
    },
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    }
  );

  const { data: total, refetch: refetchCountList } = useQuery(
    ["order.count", filters, customFilter, searches, currentUser?.branches],
    async () => {
      const response = await OrderService.count({
        companyId: currentUser?.companyId,
        userId: currentUser?.id,
        branchId: !GO_SHARE_COMPANY_IDS.includes(currentUser?.companyId!)
          ? currentUser?.branches
              ?.filter((branch) => branch.companyId === currentUser.companyId)
              ?.map((branch) => branch.id)
          : [],
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...(customFilter || {}),
        ...filters,
      });

      return response?.data || 0;
    },
    {
      enabled: !!currentUser?.id,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    }
  );

  const agentJobs: number[] = useMemo(() => {
    if (!orders || !currentUser) return [];

    const agentJobs = (orders || []).filter((job) => job.companyId !== currentUser?.companyId);

    if (!agentJobs?.length) return [];

    return agentJobs?.map((job) => job.id);
  }, [orders, currentUser]);

  useQuery(
    ["company.configs"],
    async () => {
      const agentPermissions = await CompanyConfigsService.getByCreatedCompany(agentJobs);

      if (agentPermissions?.data) {
        setAgentPermissions(agentPermissions.data);
      }

      return [];
    },
    {
      enabled: !!currentUser?.id && !!orders && !!agentJobs?.length,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
      onError: (error) => {
        console.error("Error fetching agent permissions:", error);
      },
    }
  );

  const { mutate: orderCancel, isLoading: orderCancelIsLoading } = useMutation(
    ["order.status.cancel"],
    ({ id, reason }: { id: number; reason: string }) => OrderService.cancel(id, { reason }),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        setCancelReason("");
        setOrderDelete(undefined);
        setIsSubmitted(false);
        setOpenConfirmationDeleteModal(false);
        setOrderIdToDelivered(null);
        setErrors([]);
        refetch();
        refetchCountList();
      },
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"));
        setCancelReason("");
        setOpenConfirmationDeleteModal(false);
        setOrderDelete(undefined);
        setIsSubmitted(false);
        setOrderIdToDelivered(null);
        setErrors([]);
      },
    }
  );

  const { mutate: orderDeliveryMutate, isLoading: orderDeliveryIsLoading } = useMutation(
    ["order.status.update"],
    ({ id }: { id: number }) => JobService.delivery(id),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        setCancelReason("");
        setOrderIdToDelivered(null);
        setTasksInProgress(undefined);
        setOpenConfirmationDeleteModal(false);
        setOpenWarningTasksInProgress(false);
        setOrderDelete(undefined);
        refetch();
        refetchCountList();
      },
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"));
        setCancelReason("");
        setOrderIdToDelivered(null);
        setTasksInProgress(undefined);
        setOpenConfirmationDeleteModal(false);
        setOpenWarningTasksInProgress(false);
        setOrderDelete(undefined);
      },
    }
  );

  const { mutate: orderStatusMutate, isLoading: orderStatusIsLoading } = useMutation(
    ["order.status.update"],
    ({ id, status }: { id: number; status: OrderStatus }) => JobService.update(id, { status }),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        loadOrderConfirmationModalRef.current?.close();
        refetch();
        refetchCountList();
      },
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"));
      },
    }
  );

  const { mutate: sendToAMOS, isLoading: loadingSendToAmos } = useMutation(
    ["amos.service.send.order"],
    ({ jobId }: { jobId: number }) => AmosService.sendOrderToAmos(jobId),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        setOrderIdSend(undefined);
      },
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("anErrorOccurredTryAgain"));
        setOrderIdSend(undefined);
      },
    }
  );

  const showBadgeStatus = (value?: string) => <BadgeJobStatus value={value} />;

  const showBadgeType = (value?: string) => <BadgeType value={value} />;

  const showOriginsOrDestinations = (locations?: Location[]) => {
    return (
      <div className={classes.containerLocation}>
        <div className="flex-location">
          <div className="div-location-ellipsis">
            <span>
              {locations && locations?.length > 0
                ? locations[0]?.longAddress?.toString()?.trim() || ""
                : ""}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const showLocations = (jobLocations: JobLocation) => {
    if (jobLocations) {
      const uniques = formatJobLocation(jobLocations);

      const renderLocations = (location: string) => <div key={location}>{location}</div>;

      const tooltipSpread = uniques.map((location) => {
        return renderLocations(location);
      });

      return (
        <DefaultTooltip title={tooltipSpread} placement="top-start">
          <div>
            {uniques?.map((location) => {
              return location !== "" ? renderLocations(location) : <></>;
            })}
          </div>
        </DefaultTooltip>
      );
    }

    return "";
  };

  const getOrderById = (id: number | string) =>
    (orders || [])?.find((order) => Number(order.id) === Number(id));

  const editOrder = (id: string | number) => {
    const order = (orders || [])?.find((order) => order.id === id);

    if (order) {
      history.push(`${PRIVATE_ROUTES.ORDERS_MNT}/${order.id}`, {
        id: Number(order.id),
      });
    }

    return undefined;
  };

  const renderSeeMore = (id: string | number, order: Order) => {
    if (loadingSendToAmos && orderIdSend === order?.id)
      return <CircularProgress size={18} className={classes.loadingSendToAMOS} />;

    if (
      !viewOnly &&
      order?.tasks &&
      order?.tasks.length &&
      isAllied(currentUser?.company) &&
      env.NODE_DATABASE === "production"
    ) {
      return (
        <IconButton
          onClick={(event) => {
            setAnchorMoreIcon(event.currentTarget);
            setOrderIdSend(order?.id);
          }}
          size="small"
        >
          <MoreVertIcon htmlColor="#475467" fontSize="small" />
        </IconButton>
      );
    }

    return null;
  };

  const setTags = (value: string) => {
    const tags = value.split(",");

    return (
      <TagWrapper>
        {tags.map((tag: string, index: number) => (
          <Chip key={`chip-${index}`} label={tag} />
        ))}
      </TagWrapper>
    );
  };

  const columns: Column[] = [
    {
      name: "type",
      title: translate("type"),
      formatComponent: showBadgeType,
      hideSearch: true,
    },
    {
      name: "mode",
      title: translate("mode"),
      hideSearch: true,
      formatComponent: (value: string) =>
        value ? translate(value.toLowerCase()) : "",
    },
    {
      name: "reference",
      title: translate("reference"),
      formatComponent: (value: string, job: Job) => {
        const hasVip = [
          OrderVipTreatmentMode.VIP,
          OrderVipTreatmentMode.SAFETY,
          OrderVipTreatmentMode.BLUEBARK,
        ].includes(job?.vipTreatment as OrderVipTreatmentMode);

        return (
          <div className={classes.referenceBox}>
            <span>{`${job.reference}${job.sequence ? `/${job.sequence}` : ""}`}</span>
            {hasVip && (
              <VipChip
                sx={{
                  backgroundColor: colors.warning200,
                }}
                vipTreatment={job.vipTreatment!}
              />
            )}
          </div>
        );
      },
    },
    {
      name: "secondReference",
      title: translate("secondReference"),
      formatComponent: (value?: string) => {
        return `${value || "--"}`;
      },
      initialHideColumn: true,
      searchIdentifier: "second_reference",
    },
    {
      name: "customer",
      title: translate("customer"),
      formatComponent: (_customer: Customer, job: Job) => getOrderCustomerDisplayName(job),
      hideSort: disableCustomerSorting,
      associationColumnSettings: {
        whenSearching: {
          disableSorting: setDisableCustomerSorting,
          clearSorting: setSorting,
        },
        columnToSorting: "full_name",
      },
    },
    {
      name: "moveCoordinator",
      title: translate("moveCoordinator"),
      hideSort: true,
      formatText: (moveCoordinator: User) => moveCoordinator?.fullName || "",
    },
    {
      name: "volume",
      title: translate("volume"),
      hideSort: true,
      hideSearch: true,
      formatComponent: (_: number, job: Job) => {
        if (job?.customVolumeActual === null || job?.customVolumeActual === undefined)
          return calculateTotalVolume(ItemJobSubStatus.ACTUAL, measurement, job?.itemsJob);

        return `${handleGetConvertValue(
          job?.customVolumeActual || 0,
          measurement?.code,
          measurement?.decimalPlaces
        )} ${measurement?.code || Metric["m³"]}`.trim();
      },
    },
    {
      name: "origins",
      title: translate("origin"),
      hideSort: true,
      formatComponent: showOriginsOrDestinations,
    },
    {
      name: "destinations",
      title: translate("destination"),
      hideSort: true,
      formatComponent: showOriginsOrDestinations,
    },
    {
      name: "jobLocations",
      title: translate("location"),
      formatComponent: showLocations,
      hideSort: true,
      hideSearch: true,
    },
    {
      name: "tag",
      title: translate("tags"),
      formatComponent: (value: string) => {
        if (value?.trim().length) return setTags(value);

        return "";
      },
    },
    {
      name: "updatedAt",
      title: translate("lastUpdated"),
      hideSearch: true,
      hideSort: true,
      formatText: (value: string) => {
        if (value?.trim().length && currentUser?.preferences) {
          return formatDateWithUserPref(value, currentUser.preferences);
        }

        return "";
      },
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: showBadgeStatus,
      hideSearch: true,
    },
  ].map((column) => ({
    ...column,
    initialHideColumn:
      columnsToShow && columnsToShow.length
        ? !columnsToShow.includes(column.name)
        : column.initialHideColumn,
  }));

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: translate("edit"),
      key: "edit",
      buttonAndAction: (id: number) => {
        if (
          !isShowAction(currentUser, orderPermission.edit) ||
          (!isMoving(currentUser?.company) && !operationOrdersPermissions?.edit)
        )
          return null;

        if (!viewOnly) {
          const order = getOrderById(id);

          if (!hasAgentPermission(order, "edit")) {
            return null;
          }

          if (order && isOrderNeitherDeliveredNorCancelled(order.status)) {
            return (
              <Tooltip title={translate("edit") || ""} placement="top">
                <div>
                  <FiEdit
                    size={Constants.defaultGeneralActionsIconSize}
                    className={classes.button}
                    color="#475467"
                    data-cy="editButton"
                    onClick={() => (handleEdit ? handleEdit(id, order) : editOrder(id))}
                  />
                </div>
              </Tooltip>
            );
          }
        }

        return null;
      },
    },
    {
      title: translate("view"),
      key: "view",
      buttonAndAction: (id: number, job: Job) => {
        if (
          !isShowAction(currentUser, orderPermission.view) ||
          (!isMoving(currentUser?.company) && !operationOrdersPermissions?.view)
        )
          return null;

        if (!hasAgentPermission(job, "view")) {
          return null;
        }

        return (
          <Tooltip title={translate("view") || ""} placement="top">
            <div>
              <FiEye
                size={Constants.defaultGeneralActionsIconSize}
                className={classes.button}
                color="#475467"
                data-cy="viewButton"
                onClick={() => {
                  setOrder(undefined);
                  setOrderToView({ ...job, id: job?.id ?? id });
                  sideViewRef.current?.setOpenSideBar(true);
                }}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: translate("communication"),
      key: "communication",
      buttonAndAction: (id: number) => {
        if (
          !isShowAction(currentUser, orderPermission.communication) ||
          (!isMoving(currentUser?.company) && !operationOrdersPermissions?.view)
        )
          return null;

        if (!viewOnly) {
          const order = getOrderById(id);

          if (!hasAgentPermission(order, "communication")) {
            return null;
          }

          if (order && isOrderNeitherDeliveredNorCancelled(order.status)) {
            return (
              <Tooltip title={translate("communication") || ""} placement="top">
                <div>
                  <HiOutlineMail
                    size={Constants.defaultGeneralActionsIconSize}
                    className={classes.button}
                    color="#475467"
                    data-cy="communication"
                    onClick={() => {
                      communicationModalRef.current?.onOpen(id, order, order?.customer);
                    }}
                  />
                </div>
              </Tooltip>
            );
          }
        }

        return null;
      },
    },
    {
      title: translate("deliverOrder"),
      key: "deliver",
      buttonAndAction: (id: number) => {
        if (
          !isShowAction(currentUser, orderPermission.delivery) ||
          (!isMoving(currentUser?.company) && !operationOrdersPermissions?.edit)
        )
          return null;

        const order = getOrderById(id);

        if (!hasAgentPermission(order, "delivery")) {
          return null;
        }

        if (order && order.status === OrderStatus.BOOKED) {
          return (
            <Tooltip title={translate("loadOrder") || ""} placement="top">
              <div>
                <LoadOrderIcon
                  size={18}
                  color={colors.gray600}
                  onClick={() =>
                    loadOrderConfirmationModalRef.current?.open(
                      id,
                      OrderStatus.LOADED,
                      OrderStatus.BOOKED
                    )
                  }
                  data-cy="loadOrderButton"
                />
              </div>
            </Tooltip>
          );
        }
        if (!viewOnly && order && isOrderNeitherDeliveredNorCancelled(order.status))
          return (
            <Tooltip title={translate("deliverOrder") || ""} placement="top">
              <div>
                <FiTruck
                  size={Constants.defaultGeneralActionsIconSize}
                  className={classes.button}
                  color="#475467"
                  data-cy="deliveryButton"
                  onClick={() => setOrderIdToDelivered(Number(id))}
                />
              </div>
            </Tooltip>
          );

        return null;
      },
    },
    {
      title: translate("cancel"),
      key: "cancel",
      buttonAndAction: (id: number) => {
        if (
          !isShowAction(currentUser, orderPermission.cancel) ||
          (!isMoving(currentUser?.company) && !operationOrdersPermissions?.edit)
        )
          return null;

        const order = getOrderById(id);

        if (!hasAgentPermission(order, "cancel")) {
          return null;
        }

        if (!viewOnly && order && order.status === OrderStatus.BOOKED) {
          return (
            <Tooltip title={translate("cancelOrder")} placement="top">
              <div>
                <MdOutlineCancel
                  size={Constants.defaultGeneralActionsIconSize}
                  className={classes.button}
                  data-cy="canceledButton"
                  color="#F97066"
                  onClick={() => setOrderDelete(order)}
                />
              </div>
            </Tooltip>
          );
        }

        return null;
      },
    },
    {
      title: translate("analytics"),
      key: "report",
      buttonAndAction: (id: number) => {
        if (
          !isShowAction(currentUser, orderPermission.printReport) ||
          (!isMoving(currentUser?.company) && !operationOrdersPermissions?.view)
        )
          return null;

        if (!viewOnly) {
          if (idsGeneratingReport.find((idGenerating) => id === idGenerating)) {
            return (
              <CircularProgress
                size={Constants.defaultGeneralActionsIconSize}
                style={{ color: "rgb(71, 84, 103)" }}
              />
            );
          }

          const order = getOrderById(id);

          if (!hasAgentPermission(order, "printReport")) {
            return null;
          }

          const orderValid =
            order &&
            order.status !== OrderStatus.BOOKED &&
            order.status !== OrderStatus.CANCELLED &&
            order.status !== OrderStatus.IN_PROGRESS;

          if (!orderValid) {
            return (
              <Tooltip
                title={translate(
                  "checkIfAnyOfTheTasksEligibleForReportGenerationUpliftDeliveryLocalHasBeenCompletedAndHasADate"
                )}
                placement="top"
              >
                <div>
                  <FiPrinter
                    size={Constants.defaultGeneralActionsIconSize}
                    data-cy="inventoryReportButton"
                    color="#98A2B3"
                  />
                </div>
              </Tooltip>
            );
          }

          return (
            <Tooltip title={translate("generateReport")} placement="top">
              <div>
                <FiPrinter
                  size={Constants.defaultGeneralActionsIconSize}
                  className={classes.button}
                  data-cy="inventoryReportButton"
                  color="rgb(71, 84, 103)"
                  onClick={() => generateReportRef.current?.open(order)}
                />
              </div>
            </Tooltip>
          );
        }

        return null;
      },
    },
    {
      title: translate("seeMore"),
      key: "seeMore",
      buttonAndAction: renderSeeMore,
    },
  ];

  const canShowCreateAction =
    !viewOnly &&
    isShowAction(currentUser, orderPermission.create) &&
    (isMoving(currentUser?.company) || operationOrdersPermissions?.add);
  const canShowQuickOrderAction = canShowCreateAction && quickOrderEnabled;

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: canShowCreateAction
        ? canShowQuickOrderAction
          ? ["handleQuickOrder", "handleNewRecord"]
          : ["handleNewRecord"]
        : [],
    },
    handleFilterBy: {
      onChange: handleFilter,
      categories: [
        {
          name: translate("type"),
          options: jobTypes && jobTypes?.map((jobType) => jobType?.name || ""),
        },
        {
          name: translate("status"),
          options: jobStatus?.map((jobStatus) => jobStatus?.name || ""),
        },
        {
          name: translate("leadSource"),
          column: "lead_source_from",
          options: leadSourceFrom?.map((leadSourceFrom) => leadSourceFrom?.name || ""),
        },
        {
          name: translate("branchOrigin"),
          column: "branchFromId",
          options: branchesList?.map((branch) => {
            return {
              label: branch?.name ?? "",
              value: branch?.id ? String(branch?.id) : "",
            };
          }),
        },
      ],
    },
    handleSearch: {
      onSearch: (filters) => handleSearch(filters),
    },
    handleQuickOrder: canShowQuickOrderAction ? (
      <NewRecord
        data-cy="quick-order-button"
        newRecord={{
          text: translate("quickOrder"),
          tooltip: translate("quickOrder"),
          action: () => history.push(PRIVATE_ROUTES.ORDERS_QUICK),
        }}
      />
    ) : null,
    handleNewRecord: {
      text: translate("create"),
      action: () => {
        if (
          !isShowAction(currentUser, orderPermission.create) ||
          (!isMoving(currentUser?.company) && !operationOrdersPermissions?.add)
        )
          return null;

        if (handleNewRecord) handleNewRecord();

        return history.push(PRIVATE_ROUTES.ORDERS_MNT);
      },
    },
  };

  const { refetch: getOrder, isFetching: loadingOrder } = useQuery(
    ["orders.get", orderToView?.id],
    () =>
      OrderService.getById(Number(orderToView?.id), {
        isSalesApp: true,
        isNewVersion: true,
      }),
    {
      enabled: !!orderToView?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setOrder((currentOrder) =>
            getOrderFolder(translate, {
              ...currentOrder,
              ...normalizeOrderWebPayload(response.data, currentUser),
            })
          );
        }
      },
    }
  );

  useEffect(() => {
    if (!orderToView?.id) return;
    if (!inventoryData && !actualInventoryData) return;
    setOrder((currentOrder) => {
      if (!currentOrder?.id && !currentOrder?.jobId) return currentOrder;
      if (Number(currentOrder.id ?? currentOrder.jobId) !== Number(orderToView.id)) {
        return currentOrder;
      }
      return getOrderFolder(translate, {
        ...currentOrder,
        inventory: inventoryData?.quotedInventory || [],
        actualInventory: actualInventoryData?.actualInventory || [],
      });
    });
  }, [inventoryData, actualInventoryData, orderToView?.id, loadingOrder]);

  useEffect(() => {
    if (!orderToView?.id) return;
    if (!historyData?.length) return;
    setOrder((currentOrder) => {
      if (!currentOrder?.id && !currentOrder?.jobId) return currentOrder;
      if (Number(currentOrder.id ?? currentOrder.jobId) !== Number(orderToView.id)) {
        return currentOrder;
      }
      return { ...currentOrder, histories: historyData };
    });
  }, [historyData, orderToView?.id, loadingOrder]);

  const handleValidateCancelReason = (): boolean => {
    try {
      const additions = {
        reason: yup.string().required(translate("thisFieldIsRequired")),
      } as ObjectShape;

      const schema = yup.object().shape(additions);

      schema.validateSync(
        {
          reason: cancelReason,
        },
        { abortEarly: false }
      );

      setErrors([]);
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
    if (errors.length !== 0) return false;

    return true;
  };

  const submitCancelOrder = () => {
    if (orderDelete && orderDelete?.id) {
      orderCancel({
        id: Number(orderDelete?.id),
        reason: String(cancelReason),
      });
    }
  };

  const onClickCancelOrder = () => {
    setIsSubmitted(true);
    if (handleValidateCancelReason()) submitCancelOrder();
  };

  useEffect(() => {
    if (isRefetchGetAll) {
      refetch();
    }
  }, [isRefetchGetAll]);

  const onSwitchFolder = () => {
    setTypeFolder((prev) =>
      prev === ResourceName.ORDERS ? ResourceName.QUOTE : ResourceName.ORDERS
    );
  };

  const countMaterial = (): number => {
    let materials = 0;

    order?.inventory?.forEach((inventory) => {
      if (inventory.materials.length > 0) materials += 1;
    });

    return materials;
  };

  const getSidebarTitle = (): string => {
    let title = "Order";

    const jobTitle = getJobName({
      reference: orderToView?.reference ?? order?.reference,
      sequence: orderToView?.sequence ?? order?.sequence,
    });

    const fullName = getOrderCustomerDisplayName(orderToView) || getOrderCustomerDisplayName(order);

    if (jobTitle) title = `${title} #${jobTitle}`;
    if (fullName) {
      title = `${title} - ${fullName}`;
    }

    return title;
  };

  const handleSetNavigation = useCallback(() => {
    const navList = [
      { key: Navigation.DETAILS },
      { key: Navigation.INVENTORY },
      { key: Navigation.MATERIAL },
      { key: Navigation.TASKS },
      { key: Navigation.ACCESS },
      { key: Navigation.INSTRUCTIONS },
      { key: Navigation.SHIPMENT },
      { key: Navigation.FOLDERS },
      // { key: Navigation.ESTIMATE }, // TODO Camila: wating for estimate screen to be wating
    ];

    return navList;
  }, [order]);

  const handleSetTabs = useCallback(() => {
    const tabs = [
      {
        key: Navigation.DETAILS,
        loading: loadingOrder,
        isEmpty: !order,
        render: () => <Details order={order} />,
        renderLoading: () => <SkeletonDetails />,
      },
      {
        key: Navigation.INVENTORY,
        loading: loadingOrder || isLoadingInventory || isLoadingActualInventory,
        isEmpty:
          (!order?.inventory && !order?.actualInventory) ||
          (order?.inventory?.length === 0 && order?.actualInventory?.length === 0),
        render: () => (
          <OrderInventory
            order={order}
            inventory={order?.inventory || []}
            quoteJobId={order?.jobId || 0}
            defaultMode={order?.mode || ""}
            destinations={order?.destination?.locations || []}
            actualInventory={order?.actualInventory || []}
            callbackFunction={getOrder}
            storageBoxes={order?.storageBoxes}
            quotedViewOnly
            isOnViewMode
          />
        ),
      },
      {
        key: Navigation.MATERIAL,
        loading: loadingOrder || isLoadingInventory || isLoadingActualInventory,
        isEmpty: !order?.inventory || countMaterial() === 0,
        renderLoading: () => <SkeletonDetails />,
        render: () => <PackingMaterials inventory={order?.inventory} isOnViewMode />,
      },
      {
        key: Navigation.TASKS,
        loading: loadingOrder,
        isEmpty:
          (!order?.tasks && !order?.taskDates) ||
          (order?.tasks?.length === 0 && order?.taskDates?.length === 0),
        render: () => <TasksTab tasks={order?.tasks} taskDates={order?.taskDates} isOnViewMode />,
      },
      {
        key: Navigation.ACCESS,
        loading: loadingOrder,
        isEmpty: !order?.pickupAccess && !order?.deliveryAccess,
        renderLoading: () => <SkeletonDetails />,
        render: () => (
          <AccessTab
            pickupAccess={order?.pickupAccess}
            deliveryAccess={order?.deliveryAccess}
            pickupAddress={order?.origin?.locations?.[0]}
            deliveryAddress={order?.destination?.locations?.[0]}
          />
        ),
      },
      {
        key: Navigation.INSTRUCTIONS,
        loading: loadingOrder,
        isEmpty: !order?.instructions || order?.instructions?.length === 0,
        renderLoading: () => <SkeletonDetails />,
        render: () => <Instructions isOnViewMode instructions={order?.instructions || []} />,
      },
      {
        key: Navigation.SHIPMENT,
        loading: loadingOrder,
        isEmpty: order?.shipments?.length === 0,
        render: () => <WaybillTab waybills={order?.shipments ?? []} isOnViewMode />,
      },
      {
        key: Navigation.FOLDERS,
        loading: isOrderFolderData || isQuoteFolderData,
        render: () => (
          <Files
            isOnViewMode
            inventory={typeFolder === ResourceName.QUOTE ? order?.inventory : undefined}
            folders={typeFolder === ResourceName.QUOTE ? quoteFolderData : orderFolderData}
            foldersDeleted={order?.foldersDeleted}
            filesDeleted={order?.filesDeleted}
            typeFolder={typeFolder}
            nextTypeFolder={
              typeFolder === ResourceName.ORDERS ? ResourceName.QUOTE : ResourceName.ORDERS
            }
            onSwitchTypeFolder={onSwitchFolder}
          />
        ),
      },
    ];

    return tabs;
  }, [order, typeFolder]);

  const handleSetButtonsComponent = useCallback(() => {
    return [
      {
        buttonLabel: translate("seeActivity"),
        titlePage: translate("activity"),
        color: Color.SECONDARY,
        icon: <FiActivity size={16} />,
        renderLoading: () => <SkeletonDetails />,
        renderComponent: () => (
          <History
            items={getItemsToHistory(currentUser ?? ({} as User), order?.id, order?.histories)}
          />
        ),
      },
    ];
  }, [order]);

  return (
    <div className={className || classes.mainDiv} data-cy="orderListGrid">
      {/* {showTitle !== false && <TitlePage title={translate("orders")} />} */}
      <div className={classNameContent || classes.content}>
        <ShipeeziGrid
          columns={columns}
          data={orders || []}
          totalItems={total}
          loadingData={isLoading}
          namePage={translate("orders")}
          emptyData={
            isLoading ? `${translate("loadingOrders")}...` : translate("noInformationToBeShown")
          }
          pageSize={pageSize}
          pageSizeList={[20, 40, 60, 100]}
          onChangePageSize={handlePageSize}
          currentPage={page}
          onNextPage={handleCurrentPage}
          onPreviousPage={handleCurrentPage}
          onGoToPage={handleCurrentPage}
          onSorting={handleSort}
          rowActionsExtra={rowActionsExtra}
          positionActionsColumn="right"
          generalActions={generalActions}
          onChangeColumns={(columns) =>
            window.localStorage.setItem(SELECTED_COLUMNS_ORDERS, JSON.stringify(columns))
          }
        />
      </div>

      <Menu
        id="long-menu"
        anchorEl={anchorMoreIcon}
        keepMounted
        open={!!(anchorMoreIcon && orderIdSend)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        onClose={() => {
          setAnchorMoreIcon(null);
        }}
      >
        <MenuItem
          key="sendToAmos"
          onClick={() => {
            sendToAMOS({ jobId: Number(orderIdSend) });
            setAnchorMoreIcon(null);
          }}
        >
          <span className={classes.menuItemText}>{translate("sendToAmos")}</span>
        </MenuItem>
      </Menu>

      <ConfirmationModal
        open={openConfirmationDeleteModal}
        closeModal={() => {
          setOpenConfirmationDeleteModal(false);
          setOrderDelete(undefined);
          setCancelReason("");
          setIsSubmitted(false);
        }}
        type="error"
        title={translate("cancelOrder")}
        leftIconTitle={
          <MdOutlineCancel color="#F04438" size={Constants.defaultGeneralActionsIconSize} />
        }
        loading={orderCancelIsLoading}
        customColorCancel={Color.TEXT_INFO}
        customColorSave={Color.ERROR}
        textCancel={translate("back")}
        textSubmit={translate("yesCancelOrder")}
        submit={onClickCancelOrder}
      >
        <Grid container spacing={2} className="mt-2">
          <Grid item className={classes.cancelModalContainer}>
            <div data-cy="confirmationModalCancelBody" className={classes.descriptionModalConfirm}>
              <span>
                {translate("areYouSureYouWantToCancelTheOrder")}{" "}
                {orderDelete?.reference ? orderDelete.reference : ""}?
              </span>
              <Input
                label={translate("reason")}
                placeholder={translate("reason")}
                multiline
                fullWidth
                minRows={2}
                maxRows={3}
                maxLength={255}
                value={cancelReason}
                error={fieldHasError("reason")}
                errorMessage={getErrorByField("reason")}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setCancelReason(event.target.value)
                }
              />
              <span className="error">{translate("thisActionIsPermanentAndCannotBeUndone")}</span>
            </div>
          </Grid>
        </Grid>
      </ConfirmationModal>

      <TasksInProgressModal
        open={openWarningTasksInProgress}
        tasksInProgress={tasksInProgress}
        orderDelete={orderDelete}
        onClose={() => {
          setOpenWarningTasksInProgress(false);
          setOrderDelete(undefined);
        }}
      />

      <LoadOrderConfirmationModal
        ref={loadOrderConfirmationModalRef}
        onSubmit={orderStatusMutate}
        loadingAction={orderStatusIsLoading}
      />

      <ConfirmationModal
        open={!!orderIdToDelivered}
        title={translate("deliverOrder")}
        textSubmit={translate("confirm")}
        leftIconTitle={<FiTruck color="#12B76A" size={Constants.defaultGeneralActionsIconSize} />}
        closeModal={() => setOrderIdToDelivered(null)}
        loading={orderDeliveryIsLoading}
        submit={() =>
          orderDeliveryMutate({
            id: Number(orderIdToDelivered),
          })
        }
      >
        <Grid container spacing={2} className="mt-2 ml-2">
          <Grid item>
            <div
              data-cy="confirmationModalDeliveredBody"
              className={classes.descriptionModalConfirm}
            >
              <span>{translate("areYouSureYouWantToDeliverThisOrder")}</span>
            </div>
          </Grid>
        </Grid>
      </ConfirmationModal>

      <SideViewComponent
        ref={sideViewRef}
        job={{
          ...order,
          folders: typeFolder === ResourceName.QUOTE ? quoteFolderData : orderFolderData,
        }}
        isLoading={loadingOrder}
        title={getSidebarTitle()}
        navigation={handleSetNavigation()}
        tabs={handleSetTabs()}
        buttonsComponent={handleSetButtonsComponent()}
        onClose={() => {
          setOrderToView(undefined);
          setOrder(undefined);
        }}
      />

      <SendToCustomerModalOrder ref={communicationModalRef} />

      <GenerateReportModal ref={modalRef} />

      <GenerateReportDrawer ref={generateReportRef} />
    </div>
  );
};

export default OrderOperations;
